import moment from "moment"
import { formatBytes } from "@/helpers"
import { computeComparison } from "@/common/helpers/comparisonMetrics.helper"

// ---------------------------------------------------------------------------
// Summary totals builder
// ---------------------------------------------------------------------------

/**
 * Build a flat totals object from a podcast analytics summary.
 * The output keys align with the metric names used by AnalyticsPodcastsTotals.
 *
 * @param {Object|null} summary  The `summaryStats` from the API response
 * @returns {Object} Flat totals
 */
export function computePodcastTotals(summary) {
  if (!summary || !summary.totalBytes) return emptyTotals()

  const streamedDuration = summary.streamedDuration || 0
  const uniqueListeners = summary.uniqueListeners || 0

  return {
    downloads: summary.downloads || 0,
    traffic: formatBytes(summary.downloadedBytes || 0),
    trafficRaw: summary.downloadedBytes || 0,
    averageListening: uniqueListeners
      ? +moment.duration(streamedDuration / uniqueListeners, "seconds").asMinutes().toFixed(2)
      : 0,
    totalListening: streamedDuration
      ? +moment.duration(streamedDuration, "seconds").asHours().toFixed(2)
      : 0,
    downloadPercentage: +(summary.downloadPercentage || 0).toFixed(2),
  }
}

function emptyTotals() {
  return {
    downloads: 0,
    traffic: "0 B",
    trafficRaw: 0,
    averageListening: 0,
    totalListening: 0,
    downloadPercentage: 0,
  }
}

// ---------------------------------------------------------------------------
// Comparison computation (delegates to shared helper)
// ---------------------------------------------------------------------------

/** Numeric keys safe for comparison (excludes formatted `traffic` string). */
const COMPARISON_KEYS = ["downloads", "trafficRaw", "averageListening", "totalListening", "downloadPercentage"]

/**
 * Given primary and comparison totals, produce metrics with
 * { value, comparisonValue, delta, percentChange, trend } for every key.
 *
 * @param {Object} current   Primary totals from computePodcastTotals()
 * @param {Object} previous  Comparison totals from computePodcastTotals()
 * @returns {Object} Keyed by metric name
 */
export function computePodcastComparison(current, previous) {
  return computeComparison(current, previous, COMPARISON_KEYS)
}

